import { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDeleteUserGroup, useSaveUserGroup, useScopeCatalog, useUserGroups } from '@/hooks/useUserGroups';
import { apiErrorMessage } from '@/services/api.service';
import type { UserGroup, UserGroupInput } from '@/services/userGroup.service';
import { MetricVisibilityEditor, RowRestrictionsEditor, notifyRestrictionChecks } from './ScopeEditors';

const EMPTY_GROUP: UserGroupInput = {
  name: '',
  description: '',
  blacklisted_metric_ids: [],
  row_restrictions: [],
};

function GroupDialog({
  group,
  open,
  onOpenChange,
}: {
  group: UserGroup | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: catalog, isLoading: catalogLoading } = useScopeCatalog({ enabled: open });
  const saveMutation = useSaveUserGroup();
  const [draft, setDraft] = useState<UserGroupInput>(EMPTY_GROUP);

  useEffect(() => {
    if (open) {
      setDraft(group ? {
        name: group.name,
        description: group.description,
        blacklisted_metric_ids: group.blacklisted_metric_ids,
        row_restrictions: group.row_restrictions,
      } : EMPTY_GROUP);
    }
  }, [open, group]);

  const handleSave = useCallback(async () => {
    try {
      const result = await saveMutation.mutateAsync({ id: group?.id, group: draft });
      toast.success(group ? 'Group saved' : 'Group created');
      notifyRestrictionChecks(result.restriction_checks);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to save group'));
    }
  }, [draft, group, saveMutation, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{group ? `Edit ${group.name}` : 'New group'}</DialogTitle>
          <DialogDescription>
            Everyone in this group gets these restrictions, on top of any set on them individually.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="py-4 overflow-y-auto min-h-0 space-y-4">
          <div className="space-y-3 max-w-xl">
            <div className="space-y-1">
              <Label htmlFor="group-name">Name</Label>
              <Input id="group-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="group-description">Description</Label>
              <Textarea
                id="group-description"
                rows={2}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </div>
          </div>
          <Tabs defaultValue="rows">
            <TabsList>
              <TabsTrigger value="rows">Row Restrictions</TabsTrigger>
              <TabsTrigger value="metrics">Metric Visibility</TabsTrigger>
            </TabsList>
            <TabsContent value="rows">
              <RowRestrictionsEditor
                restrictions={draft.row_restrictions}
                onChange={(row_restrictions) => setDraft({ ...draft, row_restrictions })}
                dimensions={catalog?.dimensions ?? []}
                metrics={catalog?.metrics ?? []}
                hiddenMetricIds={draft.blacklisted_metric_ids}
              />
            </TabsContent>
            <TabsContent value="metrics">
              <MetricVisibilityEditor
                metrics={catalog?.metrics ?? []}
                hiddenMetricIds={draft.blacklisted_metric_ids}
                onChange={(blacklisted_metric_ids) => setDraft({ ...draft, blacklisted_metric_ids })}
                isLoading={catalogLoading}
              />
            </TabsContent>
          </Tabs>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending || !draft.name.trim()}>
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GroupsTab() {
  const { data: groups, isLoading } = useUserGroups();
  const deleteMutation = useDeleteUserGroup();
  const [editing, setEditing] = useState<UserGroup | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openDialog = useCallback((group: UserGroup | null) => {
    setEditing(group);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback(async (group: UserGroup) => {
    if (!confirm(`Delete the group "${group.name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(group.id);
      toast.success('Group deleted');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to delete group'));
    }
  }, [deleteMutation]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          A group is a shared set of data restrictions. Each user can be in one group.
        </p>
        <Button size="sm" onClick={() => openDialog(null)}>
          <Plus className="h-4 w-4 mr-1" /> New group
        </Button>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Restrictions</TableHead>
              <TableHead className="w-[100px] text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(groups ?? []).map((g) => (
              <TableRow key={g.id}>
                <TableCell>
                  <div className="font-medium">{g.name}</div>
                  {g.description && <div className="text-xs text-muted-foreground">{g.description}</div>}
                </TableCell>
                <TableCell>{g.member_count}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {g.row_restrictions.length > 0 && (
                      <Badge variant="secondary">{g.row_restrictions.length} row restriction{g.row_restrictions.length !== 1 ? 's' : ''}</Badge>
                    )}
                    {g.blacklisted_metric_ids.length > 0 && (
                      <Badge variant="secondary">{g.blacklisted_metric_ids.length} hidden metric{g.blacklisted_metric_ids.length !== 1 ? 's' : ''}</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openDialog(g)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(g)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(groups ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  No groups yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      <GroupDialog group={editing} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
